// apps/share-web/src/live/live-model.ts
//
// Часовая модель лайва — чистое ядро. Ни таймеров, ни fetch, ни Date.now():
// всякое «сейчас» приезжает аргументом.
//
// ЗАЧЕМ МОДЕЛЬ ВООБЩЕ. Вебсокетов нет (Durable Objects к воркеру не привязан),
// поэтому расписание секций уезжает читателю ОДИН раз в нагрузке, а дальше едут
// редкие якоря позиции, и между ними телефон ВЫВОДИТ позицию сам. Замер спайка
// держит всю конструкцию: дрейф локальных часов max 8 мс, среднее 1.6 мс на 70
// якорях, при том что секция живёт 10–25 с. Для подсветки строки это невидимо.
//
// Пять правил, и каждое закрывает ошибку, которую спайк СДЕЛАЛ, а не вообразил:
//
//  1. Возраст якоря = serverNowMs − updatedAtMs. Часы телефона в этом
//     вычислении не участвуют ВООБЩЕ. Обе метки сняты одними часами —
//     серверными, — и их разность верна, как бы ни врали часы читателя.
//     Локальные часы дают только ДЕЛЬТУ от момента ответа до «сейчас», а
//     разность двух показаний одних часов от их смещения не зависит.
//  2. Позиция = posMs + возраст, если playing. ВЫВОДИТСЯ, а не накапливается:
//     rAF в фоновой вкладке замирает, а опрос нет, и при возврате картинка
//     обязана встать на место сама, а не досчитать пропущенное.
//  3. Возраст > staleAfterMs → якорь протух. Это утверждение про СВЯЗЬ, а не
//     про лист: вид кнопки меняется, ведение продолжается. Слой 2 архитектуры
//     §6a. Гостевой wifi в зале проваливается на 30–60 с посреди песни, и
//     остановить лист на этом значило бы терять место музыканта ровно в том
//     случае, ради которого фича написана. Расписание детерминировано, дрейф
//     8 мс за 70 якорей — лист остаётся ВЕРНЫМ до первого неожиданного
//     действия лидера, а его музыкант слышит ушами мгновенно.
//  4. seq монотонный. Два опроса в полёте возвращаются в обратном порядке —
//     это обычная мобильная сеть, а не гипотеза.
//  5. За duration не экстраполируем (слой 3 архитектуры §6a: экстраполировать
//     за конец песни значит врать). duration приходит СНАРУЖИ — длительности
//     песни модель не знает и выдумывать её не станет.
//
// Правило 5 названо в спеке «позиция лидера — слышимая»; там оно про десктоп
// (playhead − queue), и здесь исполнять его нечего: читатель получает уже
// слышимую позицию. Место правила 5 на этой стороне занимает граница честности
// за концом песни.
//
// ДЫРКА СПАЙКА №1 («сервер вечно отдавал playing:true, телефон уезжал вперёд
// навсегда») закрывается НЕ остановкой на устаревании, а ОГРАНИЧЕННОСТЬЮ: снизу
// её держит правило 5, а поверх — потолок LIVE_GIVE_UP_MS, потому что
// durationSecOf необязательна и без потолка протухший playing:true считался бы
// вечно.

import { sectionIndexAt, type SectionSec } from '../chart/chart-follow.js';

// ─────────────────────────── контракт провода ───────────────────────────

/** Якорь как его пишет лидер и отдаёт `GET /api/live/:id`. */
export interface LiveAnchor {
  /** Монотонный номер якоря (правило 4). */
  seq: number;
  /** Индекс песни в `payload.songs`. */
  songIdx: number;
  /** Слышимая позиция лидера на момент `updatedAtMs`. */
  posMs: number;
  playing: boolean;
  /** Когда якорь записан — часы СЕРВЕРА. */
  updatedAtMs: number;
  /**
   * Ревизия содержимого, ПОД КОТОРОЙ этот якорь принят воркером (спека §4.3).
   *
   * Отдельно от ревизии содержимого в ответе: индекс якоря относится к списку
   * той ревизии, а слова телефон держит своей. Разошлись — индекс применять
   * нельзя, иначе зал открывает чужую песню при формально свежих словах.
   * `undefined` — старый воркер, ведём как раньше.
   */
  contentRev?: number;
}

/**
 * Ответ маршрута, разобранный до состояний. Четыре «нет» различаются, потому что
 * ведут к четырём разным состояниям кнопки у читателя.
 */
export type LiveResult =
  | {
      state: 'anchor';
      anchor: LiveAnchor;
      serverNowMs: number;
      staleAfterMs: number;
      /**
       * Половина ИЗМЕРЕННОГО времени обхода: `serverNowMs` сервер поставил в
       * момент отправки, то есть примерно настолько раньше, чем телефон его
       * увидел (замер и деление надвое — `live-api.ts`). Ноль — законное
       * значение: ответ из кэша теста, мгновенный локальный воркер.
       */
      halfRttMs: number;
      /**
       * Ревизия содержимого ссылки (спека §4.2). Необязательна: воркер до
       * выката её не шлёт, и телефон обязан вести себя тогда как вчера.
       *
       * Модель её только ПЕРЕНОСИТ. Решение «перечитать и только потом
       * применить» живёт в `live-client.ts`, потому что оно про сеть и про
       * страницу, а здесь — часы.
       */
      contentRev?: number;
    }
  /** 200 с `anchor: null` — «лайв ещё не начался». Состояние, а не ошибка. */
  | { state: 'no-session'; contentRev?: number }
  /** 410 — ссылка завершена навсегда. */
  | { state: 'ended' }
  /** 404 — такой ссылки нет. */
  | { state: 'not-found' }
  /** Сеть, сервер или неразборный ответ — «попробуем ещё раз». */
  | { state: 'error' };

/** Наблюдаемое состояние связи. У кнопки на странице это разные виды. */
export type LinkState =
  /** Лайва ещё не было (или лидер его завершил) — приглушённая кнопка. */
  | 'idle'
  /** Якорь свежий, зал следит. */
  | 'live'
  /** Якорь протух — красное «нет связи» (text-share.css, tone=stale → --red). */
  | 'stale'
  /** 410: служение окончено. */
  | 'ended'
  /** 404: ссылки нет. */
  | 'gone';

/** 2.5 × heartbeat лидера. Сервер присылает своё; это — запасное. */
export const DEFAULT_STALE_AFTER_MS = 25_000;

/**
 * Потолок ведения: возраст якоря больше — позиции нет, чем бы дело ни
 * объяснялось.
 *
 * За десять минут молчания лидер не «отключился» — он ушёл: служение кончилось,
 * телефон остался лежать со вкладкой, ноутбук закрыли. Это граница между
 * «считаю за него» и «выдумываю», и без неё правило 3, перестав останавливать
 * лист, вернуло бы дырку спайка №1 целиком: durationSecOf НЕОБЯЗАТЕЛЬНА, и на
 * странице без длительностей протухший playing:true экстраполировался бы
 * вечно.
 *
 * Отдельной экспортируемой константой — на неё ссылается кнопка лайва.
 */
export const LIVE_GIVE_UP_MS = 600_000;

// ─────────────────────────────── модель ───────────────────────────────

/**
 * Принятый якорь вместе со снятыми в момент ответа метками.
 *
 * `ageAtResponseMs` хранится ПОСЧИТАННЫМ, а не парой меток, и это не экономия:
 * так серверные часы физически не могут утечь дальше этой строки, а локальные —
 * попасть в тот же вычислительный шаг. Правило 1 становится свойством типа, а не
 * дисциплиной вызывающего.
 */
export interface HeldAnchor {
  anchor: LiveAnchor;
  /** serverNowMs − updatedAtMs. Обе метки — серверные (правило 1). */
  ageAtResponseMs: number;
  /** Локальные часы в момент ответа. Годятся ТОЛЬКО как начало дельты. */
  localAtResponseMs: number;
  staleAfterMs: number;
}

export interface LiveModel {
  /** null — якоря нет: лайв не начинался, завершён или ссылка мертва. */
  held: HeldAnchor | null;
  /** Ответ, после которого состояние ссылки уже не изменится содержимым. */
  terminal: 'ended' | 'gone' | null;
}

export const emptyLiveModel = (): LiveModel => ({ held: null, terminal: null });

/**
 * Новый ответ → новая модель. Здесь живёт правило 4.
 *
 * `error` НЕ трогает удержанный якорь: провал опроса — это слой 1/2 §6a, лист
 * продолжает вестись по расписанию, и читатель об этом даже не узнаёт, пока
 * якорь не протухнет по правилу 3. Обнулять якорь на первой же неудаче значило
 * бы гасить ведение от одного потерянного пакета в зале с плохим Wi-Fi.
 */
export function applyResult(model: LiveModel, res: LiveResult, localNowMs: number): LiveModel {
  switch (res.state) {
    case 'anchor': {
      // ПРАВИЛО 4. Меньший seq выбрасывается ЦЕЛИКОМ — не только позиция:
      // опоздавший ответ нёс и старый возраст, и приняв его, мы объявили бы
      // сессию протухшей на основании уже опровергнутого.
      if (model.held && res.anchor.seq < model.held.anchor.seq) return model;
      return {
        held: {
          anchor: res.anchor,
          // ПРАВИЛО 1: возраст на момент отправки — только серверные метки, обе.
          // Плюс путь ответа: пока он шёл, якорь старел, и эти миллисекунды —
          // ИЗМЕРЕННАЯ длительность (`halfRttMs`, live-api.ts), а не поправка на
          // глазок. Правило 1 запрещает смешивать ПОКАЗАНИЯ разных часов;
          // длительность, снятая одними часами, от их смещения не зависит — тем
          // же аргументом ниже считается дельта между опросами.
          ageAtResponseMs: res.serverNowMs - res.anchor.updatedAtMs + res.halfRttMs,
          localAtResponseMs: localNowMs,
          staleAfterMs: res.staleAfterMs,
        },
        terminal: null,
      };
    }
    // Лидер завершил лайв (DELETE /api/live/:id), а ссылка продолжает читаться.
    // Держать прошлый якорь значило бы вести зал по позиции, которую больше
    // никто не подтверждает.
    case 'no-session': return { held: null, terminal: null };
    case 'ended': return { held: null, terminal: 'ended' };
    case 'not-found': return { held: null, terminal: 'gone' };
    case 'error': return model;
  }
}

/**
 * Возраст якоря СЕЙЧАС (правило 1 + правило 2).
 *
 * Серверная часть посчитана один раз при ответе; локальные часы добавляют ровно
 * дельту от того момента.
 */
export function anchorAgeMs(held: HeldAnchor, localNowMs: number): number {
  return held.ageAtResponseMs + (localNowMs - held.localAtResponseMs);
}

/**
 * ПРАВИЛО 3 — «якорь протух». Управляет ВИДОМ КНОПКИ и каденцией опроса, но НЕ
 * ведением листа: `derivePosition` этой функции не зовёт вовсе.
 */
export function isStale(held: HeldAnchor, localNowMs: number): boolean {
  return anchorAgeMs(held, localNowMs) > held.staleAfterMs;
}

export function linkStateOf(model: LiveModel, localNowMs: number): LinkState {
  if (model.terminal) return model.terminal;
  if (!model.held) return 'idle';
  return isStale(model.held, localNowMs) ? 'stale' : 'live';
}

/**
 * Позиция для `PositionSource.now()` — или null, когда вести нечем.
 *
 * УСТАРЕВАНИЯ В ЭТОМ СПИСКЕ НЕТ, и это главное решение модуля. Протухший якорь
 * гасит кнопку (`linkStateOf` → `stale`), но не лист: провал связи на 30–60 с —
 * обычная икота гостевого wifi, а расписание секций у телефона уже есть, и
 * выведенная по нему позиция остаётся верной (слой 2 §6a). Останавливаться на
 * ней значило бы отнимать у музыканта место ровно в том случае, ради которого
 * фича написана.
 *
 * null отдаётся тогда, когда ведение перестаёт быть ПРАВДОЙ, а не когда молчит
 * сеть: якоря нет вовсе, песня кончилась (правило 5) или лидер молчит дольше
 * потолка. Ведение при null просто замирает, подсветка остаётся на последней
 * секции, лист переходит читателю — это честнее выдуманной позиции.
 */
export function derivePosition(
  model: LiveModel,
  localNowMs: number,
  durationSecOf?: (songIdx: number) => number | null,
): { songIdx: number; posSec: number; playing: boolean } | null {
  const held = model.held;
  if (!held) return null;

  // ПОТОЛОК. Не правило 3: тот управляет кнопкой и лист не трогает. Здесь —
  // граница честности по времени, парная границе по песне ниже.
  if (anchorAgeMs(held, localNowMs) > LIVE_GIVE_UP_MS) return null;

  // ПРАВИЛО 2: на паузе позиция стоит — сколько бы ни прошло времени.
  const posMs = held.anchor.playing
    ? held.anchor.posMs + anchorAgeMs(held, localNowMs)
    : held.anchor.posMs;
  const posSec = posMs / 1000;

  // ПРАВИЛО 5. Длительность спрашивается про ТУ песню, что в якоре: лидер мог
  // уйти на следующую, и длительность прошлой к ней отношения не имеет.
  const duration = durationSecOf?.(held.anchor.songIdx) ?? null;
  if (duration !== null && posSec > duration) return null;

  return { songIdx: held.anchor.songIdx, posSec, playing: held.anchor.playing };
}

// ─────────────────────────────── каденция ───────────────────────────────
//
// Числа лайва, которые Сергей может захотеть покрутить, живут в двух местах, и
// это не рассыпанность: здесь — ЦЕНА (частота запросов от всего зала), а в
// `text-share/live-follow.ts` — ПОВЕДЕНИЕ страницы (`LIST_GRACE_MS`, пауза
// возврата из списка). Утащить второе сюда значило бы, что чистое ядро часов
// знает про крестик у слоя чтения.

/**
 * ЖДЁМ НАЧАЛА, и на страницу смотрят — 5 с.
 *
 * Переход «лайва нет → лайв идёт» — единственный момент на этой странице,
 * который обязан быть мгновенным, и одна константа на всё «вне лайва» была
 * ошибкой ВЫБОРА, а не логики: страница подключается сама, но при 30 с до
 * получаса секунды не знала, что подключаться уже есть к чему. На живой приёмке
 * это читалось как «нажал лайв на десктопе — на телефоне ничего, надо обновить
 * страницу» и «из списка песен не перекидывает, когда нажал play» — два симптома
 * одной причины; перезагрузка «помогала» ровно потому, что зовёт опрос
 * немедленно.
 *
 * Цена — арифметикой, и она РАСТЁТ; это не экономия. Рычаг цены в волне один —
 * каденция опроса (шапка плана), — и здесь он ухудшен сознательно: десять
 * телефонов, открытых за 15 минут до служения, дают 10 × 900/5 = 1800 запросов
 * против 10 × 900/30 = 300 при прежних 30 с. Мерка — само служение: 90 минут при
 * `POLL_LIVE_MS` это 10 × 5400/10 = 5400 запросов. То есть отзывчивость в самый
 * важный момент стоит треть бюджета служения — и стоит того: телефон лежит на
 * стойке, музыкант смотрит на него, служение начинается ровно здесь.
 */
export const POLL_WAITING_MS = 5_000;
/**
 * Всё остальное вне лайва — 30 с.
 *
 * Три случая, и ни в одном частота не покупает ничего:
 *  • вкладка СКРЫТА — телефон в кармане, смотреть некому; а первый же взгляд
 *    приходит на свежий якорь и так, потому что возврат из фона опрашивает вне
 *    очереди (`visibilitychange` → `poll()`);
 *  • якорь ПРОТУХ — либо сервер недостижим (запросы вообще не доходят), либо
 *    молчит лидер, и тогда частить значит платить за его молчание всем залом;
 *    лист при этом продолжает идти по расписанию (слой 2 §6a), то есть терять
 *    нечего;
 *  • 404 — опрос продолжается вяло, чтобы ожить после выката воркера без
 *    перезагрузки страницы.
 */
export const POLL_SLOW_MS = 30_000;
/** Лайв идёт: 10 с — тот же период, что и heartbeat лидера. */
export const POLL_LIVE_MS = 10_000;
/**
 * Пол внеплановых опросов. Секция в 200 мс — законная запись в аранжировке
 * (обрыв, стоп-тайм), и без пола такой стык означал бы опрос каждые 200 мс со
 * всего зала. Цена лайва определяется опросом читателей, а не числом ссылок.
 */
export const MIN_POLL_MS = 1000;

/**
 * Через сколько опрашивать снова.
 *
 * Внеплановый опрос к границе текущей секции — единственная роскошь здесь, и
 * стоит она немного: расписание известно локально, а худшее, что теряется без
 * него, — задержка до 10 с на НЕОЖИДАННОМ скачке лидера (пауза, перемотка,
 * смена песни). Ожидаемый ход песни модель и так выводит сама.
 *
 * Каденцию решает СОСТОЯНИЕ СВЯЗИ, а не «есть ли позиция» (за концом песни
 * позиции нет, а лидер точно двинулся дальше — там 30 с обиднее всего). Поэтому
 * на входе сам `LinkState`, а не два вывода из него: `live` и `stale` при одном
 * булеве «не лайв» получили бы одну каденцию, а стоят они разного.
 *
 * `visible` имеет цену РОВНО в ожидании начала: там разница между 5 и 30 с — это
 * разница между «служение началось само» и «обнови страницу». В лайве на телефон
 * смотрят заведомо, а при протухшем якоре частота не покупает ничего
 * (`POLL_SLOW_MS`). Оба поля обязательные: вызывающий, забывший спросить, молча
 * получил бы не ту половину выбора.
 */
export function nextPollDelayMs(args: {
  state: LinkState;
  boundaryInMs: number | null;
  visible: boolean;
}): number {
  const base = args.state === 'live'
    ? POLL_LIVE_MS
    : (args.state === 'idle' && args.visible ? POLL_WAITING_MS : POLL_SLOW_MS);
  if (args.boundaryInMs === null || args.boundaryInMs >= base) return base;
  return Math.max(MIN_POLL_MS, args.boundaryInMs);
}

/**
 * Сколько миллисекунд до ближайшей границы расписания, или null — границ
 * впереди нет.
 *
 * Границы берутся ТЕМ ЖЕ `sectionIndexAt`, которым чарт решает, что
 * подсвечивать. Своя копия правила «начало секции ей принадлежит, конец уже
 * нет» означала бы, что опрос назначается к одному стыку, а лист переезжает на
 * другом.
 */
export function msToNextBoundary(sections: readonly SectionSec[], posSec: number): number | null {
  const i = sectionIndexAt(sections, posSec);
  if (i >= 0) return (sections[i].endSec - posSec) * 1000;
  // Вне расписания — до первой секции или в дыре между ними. Ближайшая
  // граница впереди это начало следующей секции; за последней границ нет.
  let best: number | null = null;
  for (const s of sections) {
    if (s.startSec <= posSec) continue;
    const ms = (s.startSec - posSec) * 1000;
    if (best === null || ms < best) best = ms;
  }
  return best;
}
